import { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import axios from "axios";
import { API } from "@/config";
import { HardDrive, Image, Video, FileText, Loader2 } from "lucide-react";

function formatBytes(bytes) {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export default function StorageUsageCard({ token }) {
    const [usage, setUsage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        async function fetchUsage() {
            try {
                const response = await axios.get(`${API}/usage/storage`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setUsage(response.data);
            } catch (err) {
                console.error("Failed to fetch storage usage:", err);
                setError("Failed to load storage data");
            } finally {
                setLoading(false);
            }
        }
        fetchUsage();
    }, [token]);

    if (loading) {
        return (
            <div className="card" style={{ marginTop: '1.5rem' }}>
                <div className="flex items-center justify-center py-8">
                    <Loader2 className="animate-spin text-slate-400" size={24} />
                </div>
            </div>
        );
    }

    if (error || !usage) {
        return (
            <div className="card" style={{ marginTop: '1.5rem' }}>
                <div className="text-center py-6 text-slate-500 text-sm">{error || "No data available"}</div>
            </div>
        );
    }

    const items = [
        { key: 'photo', label: 'Photos', icon: Image, color: 'text-blue-500' },
        { key: 'video', label: 'Videos', icon: Video, color: 'text-purple-500' },
        { key: 'pdf', label: 'PDFs', icon: FileText, color: 'text-red-500' }
    ];

    return (
        <div className="card" style={{ marginTop: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <HardDrive size={20} />
                Media Storage
            </h2>

            {/* Total usage summary */}
            <div className="bg-slate-50 p-4 rounded-lg mb-4 border border-slate-100">
                <div className="flex justify-between items-center">
                    <div>
                        <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-1">Total Usage</div>
                        <div className="text-2xl font-bold text-slate-900">{formatBytes(usage.total_bytes)}</div>
                    </div>
                    <div className="text-right">
                        <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-1">Files</div>
                        <div className="text-xl font-semibold text-slate-700">{usage.total_count}</div>
                    </div>
                </div>
            </div>

            {/* Breakdown by type */}
            <div className="space-y-3">
                {items.map(({ key, label, icon: Icon, color }) => {
                    const typeUsage = usage.by_type[key];
                    return (
                        <div key={key} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-3">
                                <Icon size={18} className={color} />
                                <span className="font-medium text-slate-700">{label}</span>
                            </div>
                            <div className="text-right">
                                <span className="font-semibold text-slate-800">{formatBytes(typeUsage.bytes)}</span>
                                <span className="text-xs text-slate-500 ml-2">({typeUsage.count} files)</span>
                            </div>
                        </div>
                    );
                })}
            </div>
            <p className="text-xs text-slate-400 mt-4 text-center">
                Storage used by appointment notes (photos, videos, PDFs)
            </p>
        </div>
    );
}

StorageUsageCard.propTypes = {
    token: PropTypes.string
};
